import { HubDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useImmer } from 'use-immer';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import { useQueryListHubsDetails } from '../../hubs.queries';
import { useQueryListHubs } from '../../hubs.queries';
import { TowerModelUtils } from '../../logic/tower-model.utils';
import { HubDetails } from '../../models/hub-details.model';
import { useCallback, useEffect, useMemo } from 'react';
import {
  TimelineUpdatesSubscriptionCallback,
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionStream
} from '../../../../events/timelineUpdates/timelineUpdates.types';
import { useTimelineUpdatesEventCentre } from '../../../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import { TimelineUpdateTypeTag } from '@groundfloor-platform/types-event-grid-events';
import { naturalSort } from '../../../../core/util/natural-sort.util';
import { LockerDetailsActions } from '../../details/actions/locker-details.actions';
import { useHasDoubleSidedTower } from './use-has-double-sided-tower.hook';

interface State {
  currentAction: LockerDetailsActions | null;
  currentActionPayload: any;
  selectedDoubleSidedLockerSides: { hubID: string; selectedSide: string | undefined; isInverted: boolean }[];
}

const initialState: State = {
  currentAction: null,
  currentActionPayload: undefined,
  selectedDoubleSidedLockerSides: []
};

export function useLockersList(projectID: string) {
  const [state, setState] = useImmer(initialState);

  const setAction = useCallback(
    (action: LockerDetailsActions | null, payload?: any) => {
      setState((draft) => {
        draft.currentAction = action;
        if (action) {
          draft.currentActionPayload = payload;
        }
        if (action === null) {
          draft.currentActionPayload = undefined;
        }
      });
    },
    [setState]
  );

  const project = useQueryGetProject(projectID);

  const hubDtos = useQueryListHubs(projectID, { includeAppearsOn: true });
  const hubDetailsList = useQueryListHubsDetails(projectID, { includeAppearsOn: true });

  const projectIDsInScope = useMemo(
    () => (hubDtos.data || []).flatMap((p) => p.acceptsDeliveriesForProjectIDs),
    [hubDtos.data]
  );

  // subscribe to timeline update events
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    hubDetailsList.refetch();
  }, [hubDetailsList]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      if (
        event.data.projectID &&
        projectIDsInScope.includes(event.data.projectID) &&
        event.data.tags.includes(TimelineUpdateTypeTag.DeliveryUpdated)
      ) {
        return true;
      }
      return false;
    },
    [projectIDsInScope]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [],
      workflowIDs: [],
      projectIDs: projectIDsInScope
    }),
    [projectIDsInScope]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  const hubsData = useMemo(() => {
    const hubs =
      hubDtos.data
        ?.map((hubDto) => {
          const hubDetails = hubDetailsList.data?.find((h) => h.id === hubDto.id);
          if (!hubDetails) {
            return undefined;
          }
          return {
            hubDto,
            hubDetails
          };
        })
        .filter((it): it is { hubDto: HubDto; hubDetails: HubDetails } => !!it) || [];

    return naturalSort(hubs).asc((h) => h.hubDto.name);
  }, [hubDetailsList.data, hubDtos.data]);

  // Fetch tower models once, reused for each hub's double-sided check below
  const allTowerModelNames = useMemo(
    () => Array.from(new Set(hubsData.flatMap((h) => (h.hubDetails.towers ?? []).map((t) => t.modelName)))),
    [hubsData]
  );
  const { towerModels } = useHasDoubleSidedTower(allTowerModelNames);

  const hubs = {
    isLoading: hubDtos.isLoading || hubDetailsList.isLoading,
    error: hubDtos.error || hubDetailsList.error,
    data: hubsData
  };

  // Foe each Hub, set the default side selection as the "front" when any new data is laoded
  useEffect(() => {
    if (!hubDtos.data) {
      return;
    }
    setState((draft) => {
      hubDtos.data.forEach((hubDto) => {
        const current = draft.selectedDoubleSidedLockerSides.find((s) => s.hubID === hubDto.id);
        // If we have no door position mapping, or less than 2, then we don't need side selection
        if (hubDto.doorPositionMappings === undefined || hubDto.doorPositionMappings.length < 1) {
          return;
        }
        // If this hub already has a side selection, just move on
        if (current !== undefined && current.selectedSide !== undefined) {
          return;
        }
        // Get the mapping for the "front" door (the one with the lowest zPosition)
        const frontDoorMapping = [...hubDto.doorPositionMappings].sort((a, b) => a.position - b.position)[0];

        // If this hub has an entry but the selection is undefined, set it to the "front" side (zPosition === 0)
        if (current !== undefined) {
          current.isInverted = false;
          current.selectedSide = frontDoorMapping.description;
          return;
        }
        // Otherwise, add a new entry for this hub
        draft.selectedDoubleSidedLockerSides.push({
          hubID: hubDto.id,
          isInverted: false,
          selectedSide: frontDoorMapping.description
        });
      });
    });
  }, [hubDtos.data, setState]);

  // Handler for toggling the orientation of double sided towers
  function onSelectSide(hubID: string, side: string) {
    setState((state) => {
      const selectedSide = state.selectedDoubleSidedLockerSides.find((s) => s.hubID === hubID);

      const matchingDoorPositionMap = hubsData
        .find((h) => h.hubDto.id === hubID)
        ?.hubDto.doorPositionMappings?.find((d) => d.description === side);

      const isInverted = matchingDoorPositionMap?.position ? matchingDoorPositionMap?.position > 0 : false;

      if (!selectedSide) {
        state.selectedDoubleSidedLockerSides.push({
          hubID,
          selectedSide: side,
          isInverted
        });
      } else {
        selectedSide.selectedSide = side;
        selectedSide.isInverted = isInverted;
      }
    });
  }

  // Side select options for each Hub
  const sideSelectOptionsByHub = useMemo(() => {
    return hubsData.map((hubData) => {
      const towerModelNames = (hubData.hubDetails.towers ?? []).map((t) => t.modelName);
      const isDoubleSided = TowerModelUtils.hasDoubleSidedTower(towerModelNames, towerModels);
      if (!isDoubleSided) {
        return { hubID: hubData.hubDto.id, options: [] };
      }
      if (!hubData.hubDto.doorPositionMappings) {
        return { hubID: hubData.hubDto.id, options: [] };
      }
      return { hubID: hubData.hubDto.id, options: hubData.hubDto.doorPositionMappings.map((m) => m.description) };
    });
  }, [hubsData, towerModels]);

  const actions = useMemo(
    () => ({ currentAction: state.currentAction, payload: state.currentActionPayload, setAction }),
    [state.currentAction, state.currentActionPayload, setAction]
  );

  return {
    project,
    hubs,
    actions,
    state,
    onSelectSide,
    sideSelectOptionsByHub
  };
}
